/**
 * Reusable Radio Group Component
 * For rendering multiple radio buttons with shared state
 */

import React from 'react';
import Radio from './Radio';
import { clsx } from 'clsx';

export interface RadioGroupOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  options: RadioGroupOption[] | string[];
  label?: string;
  error?: string;
  helperText?: string;
  orientation?: 'horizontal' | 'vertical';
  fullWidth?: boolean;
  className?: string;
}

const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  onChange,
  options,
  label,
  error,
  helperText,
  orientation = 'vertical',
  fullWidth = true,
  className,
}) => {
  // Normalize options to array of {value, label} objects
  const normalizedOptions: RadioGroupOption[] = options.map((option) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    return option;
  });

  const handleChange = (optionValue: string) => {
    onChange?.(optionValue);
  };

  return (
    <div className={clsx({ 'w-full': fullWidth }, className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <div
        className={clsx(
          'space-y-2',
          {
            'flex flex-col space-y-2': orientation === 'vertical',
            'flex flex-row space-x-4': orientation === 'horizontal',
          }
        )}
        role="radiogroup"
        aria-labelledby={label ? `${name}-label` : undefined}
        aria-invalid={error ? 'true' : 'false'}
      >
        {normalizedOptions.map((option, index) => {
          const optionId = `${name}-${option.value}-${index}`;
          return (
            <Radio
              key={optionId}
              id={optionId}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => handleChange(option.value)}
              disabled={option.disabled}
              label={option.label}
            />
          );
        })}
      </div>
      
      {error && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p
          id={`${name}-helper`}
          className="mt-1 text-sm text-gray-500"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default RadioGroup;

